// eslint-disable-next-line no-restricted-syntax
import { useMutation } from '@apollo/client';
import type { ReactElement } from 'react';
import { Form } from '~/components/form';
import { Inputs } from '~/components/inputs';
import { Container } from '~/components/layout';
import { useDialog, useForm } from '~/hooks';
import type { User } from '~/typings/context';
import { DialogSection } from '../dialog-section.component';
import { StyledModal as Dialog } from '../dialog.style';
import { UPDATE_USER } from './update-user/update-user.queries';

export interface UserForm {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string | null;
}

interface Props {
  target: ReactElement;
  handleSubmit: (form: UserForm & { id: number }) => void;
  user: User;
}

export const EditInformationDialog = ({ target, handleSubmit, user }: Props) => {
  const { isOpen, close, TargetComponent } = useDialog(target);
  const [mutate, { loading }] = useMutation(UPDATE_USER);
  const [form, updateForm] = useForm({
    firstName: user.firstName,
    lastName: user.lastName,
    phone: user.phone,
    email: user.email,
  });

  const submit = async () => {
    await mutate({
      variables: {
        id: user.id,
        input: form,
      },
    });

    handleSubmit({
      id: user.id,
      ...form,
    });
    close();
  };

  return (
    <>
      {TargetComponent}
      <Dialog isOpen={isOpen} onDismiss={close} aria-label="Edit customer information">
        <Dialog.HeaderCentered title="Edit customer information" onClose={close} />
        <DialogSection>
          <Form onSubmit={submit}>
            <Container addInputMargin>
              <Inputs.FirstName onChange={updateForm} value={form.firstName} />
              <Inputs.LastName onChange={updateForm} value={form.lastName} />
              <Inputs.Phone
                label="Phone"
                name="phone"
                onChange={updateForm}
                value={form.phone ?? ''}
              />
              <Inputs.EmailTelesales
                onChange={updateForm}
                value={form.email}
                currentEmail={user.email}
              />
            </Container>
          </Form>
        </DialogSection>
        <Dialog.Footer
          primaryText="Save changes"
          onCancel={close}
          onSubmit={submit}
          loading={loading}
        />
      </Dialog>
    </>
  );
};
